import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Save } from "lucide-react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { sortUsersByName } from "../../Helpers/sortUsersByName";
import { PackService } from "../../services/packService";
import { PersonsService } from "../../services/personsService";
import Alert from "../components/Alert";
import Button from "../components/Button";
import DateTimeField from "../components/DateTimeField";
import { LoadingBlock } from "../components/Loading";
import PageHeader from "../components/PageHeader";
import RepeaterGroup from "../components/RepeaterGroup";
import SearchSelect from "../components/SearchSelect";
import StatusBadge from "../components/StatusBadge";
import { getViewPackPermissions } from "./viewPackPermissions";
import useValidationErrors from "../../hooks/useValidationErrors";

const toInputDate = (value) => value && moment(value).isValid() ? moment(value).format("YYYY-MM-DDTHH:mm") : "";
const toStoredDate = (value) => value && moment(value).isValid() ? moment(value).toISOString() : value;
const errorMessage = (error, fallback) => error?.response?.data?.message || error?.data?.message || error?.message || fallback;
const rowKey = () => `es-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const cleanDependentAssignments = (forms, users, packUsers) => {
  const next = JSON.parse(JSON.stringify(forms || {}));
  const internalIds = new Set([
    ...users.map((user) => user.user_id),
    ...(packUsers?.cossusers || []).map((user) => user.user_id),
  ].filter(Boolean));
  const externalNames = new Set((packUsers?.cossXusers || []).map((user) => user.name).filter(Boolean));
  const isAvailable = (person) => !person || (person.user_id ? internalIds.has(person.user_id) : externalNames.has(person.name));
  const equipment = next.equipment_placement_removal;

  if (equipment) {
    ["responsible_person", "es_placing", "es_withdrawing"].forEach((field) => {
      if (!isAvailable(equipment[field])) equipment[field] = "";
    });
    (equipment.pses || []).forEach((pse) => {
      if (!isAvailable(pse.pse_user)) pse.pse_user = "";
    });
  }
  if (next.record_of_movement?.es_user && !users.some((user) => user.user_id === next.record_of_movement.es_user.user_id)) {
    next.record_of_movement.es_user = "";
  }

  return next;
};

const swapSupervisors = (users, from, to) => {
  if (from === to || !users[from] || !users[to]) return users;
  const next = [...users];
  next[from] = { ...users[to], start_date: users[from].start_date, end_date: users[from].end_date };
  next[to] = { ...users[from], start_date: users[to].start_date, end_date: users[to].end_date };
  return next;
};

const ReassignPack = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const [pack, setPack] = useState(null);
  const [users, setUsers] = useState([]);
  const [options, setOptions] = useState([]);
  const { clearErrorsForPath, errors, setErrors } = useValidationErrors({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [packResponse, peopleResponse] = await Promise.all([
        PackService.getPackById(id),
        PersonsService.getUsersByType([3]),
      ]);
      setPack(packResponse);
      setUsers((packResponse.users?.esusers || []).map((user) => ({ ...user, _key: rowKey() })));
      setOptions(sortUsersByName(peopleResponse?.data).map((user) => ({ label: user.name, user, value: user.id })));
    } catch (requestError) {
      setError(errorMessage(requestError, "The reassignment details could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [authUser?.id, id]);

  useEffect(() => { loadPage(); }, [loadPage]);

  const permissions = getViewPackPermissions(authUser, pack);
  const period = useMemo(() => pack ? `${moment(pack.location?.planned_start_date).format("D MMM YYYY, HH:mm")} to ${moment(pack.location?.planned_end_date).format("D MMM YYYY, HH:mm")}` : "", [pack]);
  const updateUser = (index, values) => {
    Object.keys(values).forEach((field) => clearErrorsForPath(`esusers.${index}.${field}`));
    setUsers((current) => current.map((user, userIndex) => userIndex === index ? { ...user, ...values } : user));
  };
  const moveUser = (from, to) => setUsers((current) => swapSupervisors(current, from, to));

  const save = async () => {
    setSaving(true);
    setError("");
    setErrors({});
    const submittedUsers = users.map(({ _key, ...user }) => user);
    const forms = cleanDependentAssignments({
      equipment_placement_removal: pack.forms?.equipment_placement_removal || {},
      record_of_movement: pack.forms?.record_of_movement || {},
    }, submittedUsers, pack.users);

    try {
      const response = await PackService.updateESUsers(pack.id, { newPackData: {
        end_date: pack.location?.planned_end_date,
        forms,
        start_date: pack.location?.planned_start_date,
        status: pack.status,
        users: submittedUsers,
      } });
      if (!response?.success) throw new Error(response?.message || "The reassignment could not be saved.");
      navigate(`/view/${pack.id}`, { replace: true });
    } catch (requestError) {
      setErrors(requestError?.errors || requestError?.data?.errors || {});
      setError(errorMessage(requestError, "The Engineering Supervisors could not be updated."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingBlock label="Loading reassignment details" />;
  if (!pack) return <Alert title="Unable to load pack" type="error">{error}</Alert>;

  return (
    <div className="wsm-reassign-page">
      <PageHeader
        actions={<Button icon={ArrowLeft} onClick={() => navigate(`/view/${pack.id}`)} variant="outline">Back to pack</Button>}
        description="Update the Engineering Supervisors responsible for each part of the pack period."
        meta={<div className="wsm-reassign-page__meta"><strong>{pack.pack_reference || `WSP-${String(pack.pack_number).padStart(4, "0")}`}</strong><StatusBadge type="secondary">{pack.status}</StatusBadge><span>{period}</span></div>}
        title="Reassign Engineering Supervisors"
      />

      {!permissions.canReassign ? <Alert title="Reassignment unavailable" type="warning">This pack cannot be reassigned in its current status or after its planned end date.</Alert> : <>
        {error && <Alert title="Reassignment not saved" type="error">{error}</Alert>}
        <Alert title="This changes the pack workflow" type="warning">Saving will return the pack to Created. Assignments in dependent forms are removed automatically when their Engineering Supervisor is no longer included.</Alert>

        <section className="wsm-reassign-page__section">
          <div className="wsm-reassign-page__section-heading"><div><h2>Supervisor coverage</h2><p>Coverage must span the complete pack period without gaps or overlaps.</p></div><span>{users.length} assigned</span></div>
          {errors.esusers?.[0] && <Alert title="Check supervisor coverage" type="error">{errors.esusers[0]}</Alert>}
          <RepeaterGroup
            addLabel="Add Engineering Supervisor"
            itemLabel={(user, index) => user.name || `Engineering Supervisor ${index + 1}`}
            items={users.map((user) => ({ ...user, id: user._key }))}
            minItems={1}
            onAdd={() => {
              clearErrorsForPath("esusers");
              setUsers((current) => [...current, { _key: rowKey(), end_date: pack.location?.planned_end_date, name: "", start_date: current.at(-1)?.end_date || pack.location?.planned_start_date }]);
            }}
            onMove={moveUser}
            onRemove={(index) => {
              clearErrorsForPath("esusers");
              setUsers((current) => current.filter((_, userIndex) => userIndex !== index));
            }}
            renderItem={(user, index) => <div className="wsm-reassign-page__fields">
              <SearchSelect
                error={errors[`esusers.${index}.name`]?.[0]}
                label="Engineering Supervisor"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                onChange={(option) => updateUser(index, { name: option?.user?.name || "", phone_no: option?.user?.phone_no || "", user_id: option?.user?.id || "" })}
                options={options}
                required
                value={options.find((option) => option.value === user.user_id) || null}
              />
              <DateTimeField error={errors[`esusers.${index}.start_date`]?.[0]} label="Coverage start" onChange={(event) => updateUser(index, { start_date: toStoredDate(event.target.value) })} required value={toInputDate(user.start_date)} />
              <DateTimeField error={errors[`esusers.${index}.end_date`]?.[0]} label="Coverage end" onChange={(event) => updateUser(index, { end_date: toStoredDate(event.target.value) })} required value={toInputDate(user.end_date)} />
            </div>}
          />
        </section>

        <footer className="wsm-reassign-page__footer">
          <Button icon={ArrowLeft} onClick={() => navigate(`/view/${pack.id}`)} variant="outline">Cancel</Button>
          <Button icon={Save} loading={saving} onClick={save}>Save reassignment</Button>
        </footer>
      </>}
    </div>
  );
};

export { cleanDependentAssignments, swapSupervisors };
export default ReassignPack;
